import {
  ContactValue,
  DetailSection,
  DocumentPreview,
  Field,
  FieldGrid,
} from '@/components/admin/detail-parts';
import { StatusBadge } from '@/components/ui/status-badge';
import { type ModerationItem, type VerificationStatus } from '@/contracts';
import { useCityLabel } from '@/i18n/use-city-label';
import { useTranslations } from '@/i18n/use-locale';
import { isoDate } from '@/lib/detail';
import { useOrganizationKindLabel, useOrgModerationStatusLabel } from '@/lib/labels';
import { verificationTone } from '@/lib/status';
import { ModerationDecision } from './moderation-decision';
import { Card } from '@/components/ui/card';
import { HistorySection } from './moderation-history-section';

/**
 * Where an item stands, in the list's own vocabulary. The by-id read sends `status`;
 * the queue list sends the organization's status, and a document in the queue is
 * always awaiting audit.
 */
function organizationStatus(item: ModerationItem): VerificationStatus {
  if (item.status === 'approved') return 'verified';
  if (item.status === 'rejected') return 'rejected';
  if (item.status === 'pending') return 'pending';
  const status = item.organization?.status;
  if (status === 'approved') return 'verified';
  return status === 'rejected' ? 'rejected' : 'pending';
}

export function OrganizationReview({
  item,
  onDecided,
}: {
  item: ModerationItem;
  onDecided: () => void;
}) {
  const t = useTranslations('admin.review');
  const common = useTranslations('common');
  const verification = useTranslations('admin.verification');
  const kindLabel = useOrganizationKindLabel();
  const statusLabel = useOrgModerationStatusLabel();
  const cityLabel = useCityLabel();
  const org = item.organization;

  if (!org) return null;

  const status = organizationStatus(item);
  const requester = org.requestedBy;

  return (
    <>
      <Card asChild>
        <header className="p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-navy" dir="auto">
                {org.name}
              </h2>
              <p className="mt-0.5 text-[12px] text-conc-500">
                {kindLabel(org.kind)}
                {cityLabel(org.city) && ` · ${cityLabel(org.city)}`}
                {' · '}
                <span className="num">
                  {t('submittedOn', { date: isoDate(item.submittedAt) ?? '' })}
                </span>
              </p>
            </div>
            <StatusBadge tone={verificationTone(status)}>{statusLabel(status)}</StatusBadge>
          </div>
        </header>
      </Card>

      <div className="grid gap-6 xl:grid-cols-2">
        <DetailSection title={t('section.registration')}>
          <FieldGrid>
            <Field label={t('field.organizationName')}>{org.name}</Field>
            <Field label={t('field.kind')}>{kindLabel(org.kind)}</Field>
            <Field label={t('field.city')}>{cityLabel(org.city)}</Field>
            <Field label={verification('field.cr')}>
              {org.commercialRegistration && (
                <span className="num">{org.commercialRegistration}</span>
              )}
            </Field>
            <Field label={verification('field.officialEmail')} className="sm:col-span-2">
              {org.officialEmail && (
                <ContactValue value={org.officialEmail} copyLabel={common('copyEmail')} />
              )}
            </Field>
          </FieldGrid>
        </DetailSection>

        <DetailSection title={t('section.requester')}>
          <FieldGrid>
            <Field label={t('field.name')}>{requester.fullName}</Field>
            <Field label={t('field.phone')}>
              {requester.phone && (
                <ContactValue value={requester.phone} copyLabel={t('copyPhone')} numeric />
              )}
            </Field>
            <Field label={t('field.email')}>
              {requester.email && (
                <ContactValue value={requester.email} copyLabel={common('copyEmail')} />
              )}
            </Field>
          </FieldGrid>
        </DetailSection>
      </div>

      <DetailSection title={t('section.letter')}>
        {item.previewUrl ? (
          <DocumentPreview url={item.previewUrl} title={verification('ceoLetter')} />
        ) : (
          <p className="text-[12.5px] text-conc-400">{verification('noLetter')}</p>
        )}
      </DetailSection>

      <HistorySection item={item} />

      {status === 'pending' ? (
        <DetailSection title={t('section.decision')}>
          <ModerationDecision item={item} name={org.name} onDecided={onDecided} />
        </DetailSection>
      ) : null}
    </>
  );
}
